import * as Dialog from '@radix-ui/react-dialog';
import { Eye } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { useApproveJobRequest, useDeclineJobRequest } from '@/hooks/job-requests';
import { useServiceTypes } from '@/hooks/catalog-admin';
import { usePropertyCategories } from '@/hooks/property-categories';
import { ApiError } from '@/lib/api';
import { formatDay, formatWallTime } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import { AssignmentPicker } from '@/pages/admin/jobs/AssignmentPicker';
import { minutesToHours, type AdminJobRequestView } from '@shared/schemas/job-request';

/*
 * Reviewing a request — ROADMAP.md §5.5.
 *
 * **The note field says out loud that the client will read it.** That is P20:
 * the prototype shows the note verbatim to the client and nothing in its admin
 * UI says so, and the test data contains "I hate you jk lol". Harmless there,
 * and a demonstration that an internal-sounding field is client-facing.
 *
 * Approving is a job being created, so this dialog collects everything a job
 * needs — including the crew, through the same `AssignmentPicker` the create
 * dialog uses, warnings and all.
 */

/** The line that appears above every note field on this screen. */
function ClientFacingNotice() {
  return (
    <p className="flex items-start gap-2 rounded-sm border border-honey-500 bg-honey-50 px-3 py-2.5 text-meta text-bark-900">
      <Eye aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
      <span>
        <span className="font-medium">The client reads this, word for word.</span> It appears on
        their request in the portal.
      </span>
    </p>
  );
}

export function ApproveRequestDialog({
  request,
  onClose,
}: {
  request: AdminJobRequestView | null;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-2xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          {request ? <ApproveForm request={request} onDone={onClose} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ApproveForm({ request, onDone }: { request: AdminJobRequestView; onDone: () => void }) {
  const toast = useToast();
  const approve = useApproveJobRequest(request.id);
  const services = useServiceTypes({ page: 1, search: '', activity: 'active' });
  const categories = usePropertyCategories({ page: 1, search: '', activity: 'active' });

  /*
   * Everything the client proposed is a **default, not a decision**. An
   * approval that silently used a preferred date nobody looked at is how a
   * cleaner ends up somewhere unexpected — so each field is pre-filled and each
   * one is editable.
   */
  const [serviceTypeId, setServiceTypeId] = useState(request.serviceTypeId ?? '');
  const [categoryId, setCategoryId] = useState(request.categoryId ?? '');
  const [scheduledDate, setScheduledDate] = useState(request.preferredDate ?? '');
  const [scheduledTime, setScheduledTime] = useState(request.preferredTime?.slice(0, 5) ?? '09:00');
  const [durationMinutes, setDurationMinutes] = useState(
    request.estimatedDurationMinutes ?? request.serviceDefaultDurationMinutes ?? 120,
  );
  const [specialInstructions, setSpecialInstructions] = useState(request.notes ?? '');
  const [reviewNote, setReviewNote] = useState('');
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);

  const error = approve.error instanceof ApiError ? approve.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  const assignmentParams =
    scheduledDate === '' || scheduledTime === ''
      ? null
      : {
          date: scheduledDate,
          time: scheduledTime,
          durationMinutes,
          // The customer chose the address when they asked, so this is known
          // from the moment the dialog opens — unless the property has since
          // been retired, in which case distance simply is not scored.
          ...(request.propertyId === null ? {} : { propertyId: request.propertyId }),
        };

  function submit(event: FormEvent) {
    event.preventDefault();

    approve.mutate(
      {
        serviceTypeId,
        categoryId: categoryId === '' ? null : categoryId,
        scheduledDate,
        scheduledTime,
        estimatedDurationMinutes: durationMinutes,
        employeeIds,
        specialInstructions,
        reviewNote,
      },
      {
        onSuccess: (approved) => {
          toast.success(
            `Approved. Booked as ${approved.createdJobNumber ?? 'a new job'} — it is on the calendar.`,
          );
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">Approve this request</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        {request.clientName} · {request.propertyAddress ?? 'address removed'}
      </Dialog.Description>

      <div className="mt-4 rounded-sm border border-ink-300 bg-ink-100/40 px-3 py-2.5 text-meta text-ink-700">
        <p className="font-medium text-ink-900">What they asked for</p>
        <p className="mt-1">
          {request.serviceTypeName ?? 'No service chosen'} ·{' '}
          {request.preferredDate
            ? `${formatDay(request.preferredDate)}${request.preferredTime ? ` at ${formatWallTime(request.preferredTime)}` : ''}`
            : 'no preferred date'}
          {request.estimatedDurationMinutes === null
            ? ''
            : ` · they thought about ${String(minutesToHours(request.estimatedDurationMinutes))}h`}
        </p>
        {request.notes ? <p className="mt-1">{request.notes}</p> : null}
      </div>

      <form onSubmit={submit} className="mt-5 space-y-4">
        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="approve-service" required>
              Service
            </Label>
            <select
              id="approve-service"
              name="serviceTypeId"
              required
              value={serviceTypeId}
              onChange={(event) => {
                setServiceTypeId(event.target.value);
                const chosen = (services.data?.data ?? []).find(
                  (entry) => entry.id === event.target.value,
                );
                // Only when the client left the duration blank — overwriting
                // their estimate with a catalog default throws away the one
                // thing they told us about the size of the job.
                if (chosen && request.estimatedDurationMinutes === null) {
                  setDurationMinutes(chosen.defaultDurationMinutes);
                }
              }}
              className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              <option value="">Choose a service</option>
              {(services.data?.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
            {fieldErrors.serviceTypeId ? (
              <p role="alert" className="text-meta text-status-priority-text">
                {fieldErrors.serviceTypeId}
              </p>
            ) : null}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="approve-category">Checklist</Label>
            <select
              id="approve-category"
              name="categoryId"
              value={categoryId}
              onChange={(event) => {
                setCategoryId(event.target.value);
              }}
              className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
            >
              <option value="">No checklist</option>
              {(categories.data?.data ?? []).map((entry) => (
                <option key={entry.id} value={entry.id}>
                  {entry.name}
                </option>
              ))}
            </select>
            <p className="text-meta text-ink-500">Defaults to the property&rsquo;s own category.</p>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Date"
            name="scheduledDate"
            type="date"
            required
            value={scheduledDate}
            error={fieldErrors.scheduledDate}
            onChange={(event) => {
              setScheduledDate(event.target.value);
            }}
          />
          <Field
            label="Time"
            name="scheduledTime"
            type="time"
            required
            value={scheduledTime}
            error={fieldErrors.scheduledTime}
            onChange={(event) => {
              setScheduledTime(event.target.value);
            }}
          />
          <Field
            label="Minutes"
            name="estimatedDurationMinutes"
            type="number"
            min={15}
            max={1440}
            step={15}
            required
            value={durationMinutes}
            error={fieldErrors.estimatedDurationMinutes}
            onChange={(event) => {
              setDurationMinutes(Number(event.target.value));
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label>Crew</Label>
          <AssignmentPicker
            params={assignmentParams}
            value={employeeIds}
            onChange={setEmployeeIds}
          />
          <p className="text-meta text-ink-500">
            A job with nobody on it is created as pending, and can be staffed later.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="approve-instructions">Notes for the crew</Label>
          <textarea
            id="approve-instructions"
            name="specialInstructions"
            rows={2}
            value={specialInstructions}
            onChange={(event) => {
              setSpecialInstructions(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
          />
          <p className="text-meta text-ink-500">
            Starts as what the client wrote. Goes on the job, not to the client.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="approve-review-note">Note back to the client</Label>
          <ClientFacingNotice />
          <textarea
            id="approve-review-note"
            name="reviewNote"
            rows={2}
            value={reviewNote}
            onChange={(event) => {
              setReviewNote(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder="Tuesday works — see you at nine."
          />
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={approve.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={approve.isPending}>
            {approve.isPending ? 'Approving…' : 'Approve and book'}
          </Button>
        </div>
      </form>
    </>
  );
}

export function DeclineRequestDialog({
  request,
  onClose,
}: {
  request: AdminJobRequestView | null;
  onClose: () => void;
}) {
  return (
    <Dialog.Root
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          {request ? <DeclineForm request={request} onDone={onClose} /> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DeclineForm({ request, onDone }: { request: AdminJobRequestView; onDone: () => void }) {
  const toast = useToast();
  const decline = useDeclineJobRequest(request.id);
  const [reviewNote, setReviewNote] = useState('');

  const error = decline.error instanceof ApiError ? decline.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent) {
    event.preventDefault();
    decline.mutate(
      { reviewNote },
      {
        onSuccess: () => {
          toast.success('Declined, and the client has been told why.');
          onDone();
        },
      },
    );
  }

  return (
    <>
      {/* One word for the state — never "rejected", never "Not Approved". */}
      <Dialog.Title className="text-h2 text-ink-900">Decline this request</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        {request.clientName} · {request.propertyAddress ?? 'address removed'}
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <div className="space-y-1.5">
          <Label htmlFor="decline-review-note" required>
            Why
          </Label>
          <ClientFacingNotice />
          <textarea
            id="decline-review-note"
            name="reviewNote"
            rows={3}
            required
            value={reviewNote}
            onChange={(event) => {
              setReviewNote(event.target.value);
            }}
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body"
            placeholder="We are fully booked that week — could we do the Monday after?"
          />
          {fieldErrors.reviewNote ? (
            <p role="alert" className="text-meta text-status-priority-text">
              {fieldErrors.reviewNote}
            </p>
          ) : null}
          {/* P16: a decline with no reason is a phone call. */}
          <p className="text-meta text-ink-500">
            Required. A decline with no reason is a phone call to the office.
          </p>
        </div>

        <div className="flex flex-col-reverse gap-2 pt-1 sm:flex-row sm:justify-end">
          <Dialog.Close asChild>
            <Button variant="outline" disabled={decline.isPending}>
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" variant="danger" disabled={decline.isPending}>
            {decline.isPending ? 'Declining…' : 'Decline request'}
          </Button>
        </div>
      </form>
    </>
  );
}
